// Rssfeed.js
import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useDispatch, useSelector } from "react-redux";
import { loadAllTicketPaginated } from "../../../redux/rtk/features/crm/ticket/ticketSlice";
import { Modal, Pagination, message } from "antd";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import moment from "moment";
import { IoArrowForward, IoNewspaperOutline, IoSparklesOutline } from "react-icons/io5";
import umtLogo from "../../../assets/images/umt-logo.png";
import campusPulse from "../../../assets/images/campus-pulse-logo.png";
import AuthButtons from "./AuthButtons";
import PostFilterButtons, { UmtRadioCard } from "./PostFilterButtons";
import TicketCard from "./TicketCard";
import { CALENDAR_SCOPE, createCalendarEvent, deleteCalendarEvent } from "../../../utils/googleCalendar";
import "./rssFeeds.css";

const skeletonBaseColor = "#3076c9"; // Muted light blue
const skeletonHighlightColor = "#83adde"; // Pale blue-white shine

const Rssfeed = () => {
  const dispatch = useDispatch();
  const { list: ticketList, loading, total } = useSelector((state) => state.ticket);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userType, setUserType] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // Map of ticketId -> that reminder's event id on the user's Google
  // Calendar (rather than just a set of ids), so "remove" can delete the
  // matching event via the Calendar API.
  const [remindedEvents, setRemindedEvents] = useState({});
  // The Calendar-scoped access token lives only in memory for this session
  // (never localStorage) — it's short-lived and re-requested via a Google
  // consent popup once it expires.
  const calendarTokenRef = useRef(null);
  const pendingReminderRef = useRef(null);
  const [dateRange, setDateRange] = useState([null, moment()]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPostFor, setSelectedPostFor] = useState(1);
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const pageSize = 12;
  const ticketStatusId = 4; // Filter for ticket status

  useEffect(() => {
    const formattedDateRange = dateRange.map((date) =>
      date ? moment(date).format("YYYY-MM-DD") : null
    );

    dispatch(
      loadAllTicketPaginated({
        query: "all",
        page: currentPage,
        // The API's page-size param is named `count` (see queryGenerator /
        // TableComponent) — `pageSize` was silently ignored, so the backend
        // always fell back to its hardcoded default of 10 per page.
        count: pageSize,
        startDate: formattedDateRange[0] || undefined,
        endDate: formattedDateRange[1] || undefined,
        postFor: selectedPostFor !== "all" ? selectedPostFor : undefined,
        ticketStatusId, // Ensure we are only fetching tickets with status 4
      })
    );
  }, [dispatch, currentPage, dateRange, selectedPostFor]);

  // Whenever the signed-in user changes, refresh which posts they've
  // already reminded themselves about, so the button reflects prior state
  // (including across page reloads) instead of always starting "off".
  useEffect(() => {
    if (!currentUser?.email) {
      setRemindedEvents({});
      return;
    }
    axios
      .get(`ticket-reminder?userEmail=${encodeURIComponent(currentUser.email)}`)
      .then(({ data }) => {
        const nextEvents = {};
        (data?.reminders || []).forEach((reminder) => {
          nextEvents[reminder.ticketId] = reminder.googleEventId || true;
        });
        setRemindedEvents(nextEvents);
      })
      .catch(() => {
        // Non-fatal: the reminder button just won't show prior state this load.
      });
  }, [currentUser]);

  // Like counts are public; `liked` only fills in for a signed-in user.
  // Re-fetched whenever the visible posts or the signed-in user change.
  const [likeCounts, setLikeCounts] = useState({});
  const [likedIds, setLikedIds] = useState({});
  const visibleTicketIds = ticketList
    .filter((ticket) => ticket.ticketStatusId === ticketStatusId)
    .map((ticket) => ticket.ticketId)
    .join(",");

  useEffect(() => {
    if (!visibleTicketIds) return;
    const emailParam = currentUser?.email ? `&userEmail=${encodeURIComponent(currentUser.email)}` : "";
    axios
      .get(`ticket-like/summary?ticketIds=${visibleTicketIds}${emailParam}`)
      .then(({ data }) => {
        // Posts with no likes are absent from `counts`, so default them to 0.
        setLikeCounts((prev) => {
          const next = { ...prev };
          visibleTicketIds.split(",").forEach((id) => {
            next[id] = data?.counts?.[id] || 0;
          });
          return next;
        });
        const liked = {};
        (data?.liked || []).forEach((id) => {
          liked[id] = true;
        });
        setLikedIds((prev) => ({ ...prev, ...liked }));
      })
      .catch(() => {
        // Non-fatal: counts just stay hidden/stale this load.
      });
  }, [visibleTicketIds, currentUser]);

  const handleToggleLike = async (ticket) => {
    if (!currentUser?.email) {
      message.info("Sign in with your UMT Google account to like posts.");
      return;
    }
    const id = ticket.ticketId;
    const wasLiked = Boolean(likedIds[id]);

    // Optimistic update so the heart responds instantly; corrected (or
    // rolled back) once the server answers.
    setLikedIds((prev) => ({ ...prev, [id]: !wasLiked }));
    setLikeCounts((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) + (wasLiked ? -1 : 1)) }));

    try {
      const { data } = await axios.post("ticket-like/toggle", {
        ticketId: id,
        userEmail: currentUser.email,
      });
      setLikedIds((prev) => ({ ...prev, [id]: data.liked }));
      setLikeCounts((prev) => ({ ...prev, [id]: data.count }));
    } catch {
      setLikedIds((prev) => ({ ...prev, [id]: wasLiked }));
      setLikeCounts((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) + (wasLiked ? 1 : -1)) }));
      message.error("Couldn't update your like. Please try again.");
    }
  };

  const handleDateChange = (dates) => setDateRange([dates[0], moment()]);

  const hasValidCalendarToken = () => {
    const cached = calendarTokenRef.current;
    return Boolean(cached && cached.expiresAt > Date.now());
  };

  // Actually adds/removes the event on the user's Google Calendar (called
  // once a calendar-scoped access token is available) and mirrors that into
  // our own bookkeeping table so the button's state survives a reload.
  const applyReminderChange = async (ticket) => {
    const accessToken = calendarTokenRef.current?.token;
    const existingEventId = remindedEvents[ticket.ticketId];

    try {
      if (existingEventId) {
        if (typeof existingEventId === "string") {
          try {
            await deleteCalendarEvent(accessToken, existingEventId);
          } catch (calendarError) {
            // Log but don't block removing our own bookkeeping row — the
            // event may have already been deleted by the user in Google
            // Calendar directly, which Google reports as a 404/410 here.
            console.error("Google Calendar delete failed:", calendarError.response?.data || calendarError);
          }
        }
        await axios.delete(
          `ticket-reminder/${ticket.ticketId}?userEmail=${encodeURIComponent(currentUser.email)}`
        );
        setRemindedEvents((prev) => {
          const next = { ...prev };
          delete next[ticket.ticketId];
          return next;
        });
        message.success("Removed from your Google Calendar.");
      } else {
        const eventId = await createCalendarEvent(accessToken, ticket).catch((calendarError) => {
          console.error("Google Calendar create failed:", calendarError.response?.data || calendarError);
          const reason = calendarError.response?.data?.error?.message;
          throw new Error(reason ? `Google Calendar: ${reason}` : "Google Calendar request failed");
        });
        await axios
          .post("ticket-reminder", {
            ticketId: ticket.ticketId,
            userEmail: currentUser.email,
            userName: currentUser.name,
            googleEventId: eventId,
          })
          .catch((backendError) => {
            console.error("Saving reminder failed:", backendError.response?.data || backendError);
            throw new Error("Event was added to Google Calendar, but saving it on our side failed");
          });
        setRemindedEvents((prev) => ({ ...prev, [ticket.ticketId]: eventId }));
        message.success("Added straight to your Google Calendar!");
      }
    } catch (error) {
      message.error(error.message || "Couldn't update your Google Calendar. Please try again.");
    }
  };

  // Normally the token from the main sign-in is reused as-is. Only once it
  // has expired (or after a page reload, since it's never persisted) do we
  // silently re-request one, pre-selecting the already signed-in account.
  const requestCalendarAccess = useGoogleLogin({
    scope: CALENDAR_SCOPE,
    hint: currentUser?.email,
    onSuccess: (tokenResponse) => {
      calendarTokenRef.current = {
        token: tokenResponse.access_token,
        expiresAt: Date.now() + (tokenResponse.expires_in - 60) * 1000,
      };
      const pendingTicket = pendingReminderRef.current;
      pendingReminderRef.current = null;
      if (pendingTicket) applyReminderChange(pendingTicket);
    },
    onError: () => {
      pendingReminderRef.current = null;
      message.error("Calendar permission wasn't granted, so nothing was changed.");
    },
  });

  const handleToggleReminder = (ticket) => {
    if (!currentUser?.email) {
      message.info("Sign in with your UMT Google account to add reminders.");
      return;
    }

    if (hasValidCalendarToken()) {
      applyReminderChange(ticket);
    } else {
      pendingReminderRef.current = ticket;
      requestCalendarAccess();
    }
  };

  const handlePostForChange = (type) => {
    setSelectedPostFor(type);
    setCurrentPage(1);
  };

  // If a signed-in user was viewing a members-only feed (Participants /
  // Faculty-Staff) and logs out, that filter is no longer selectable —
  // fall back to the Public feed instead of leaving the view stuck.
  const handleLogout = () => {
    setSelectedPostFor(1);
    setCurrentPage(1);
    setCurrentUser(null);
    setLikedIds({});
    calendarTokenRef.current = null;
  };

  // Filter tickets in case there are other statuses in ticketList
  const filteredTickets = ticketList.filter(
    (ticket) =>
      ticket.ticketStatusId === ticketStatusId &&
      (selectedPostFor === "all" ? ticket.postFor === 1 : ticket.postFor === selectedPostFor)
  );

  return (
    <div className="page-container">
      <div className="umt-logo">
        <img src={umtLogo} alt="UMT Logo" />
        <img src={campusPulse} alt="Campus Pulse Logo" />
      </div>

      <div className="mainHeading">
        <span className="hero-eyebrow animated-eyebrow">
          <IoSparklesOutline /> UMT Campus Updates
        </span>

        <h1 className="animated-heading">
          Campus Pulse
          <span className="heading-pulse-dot" aria-hidden="true" />
        </h1>

        <p className="hero-teaser">
          Your central hub for campus news, events & opportunities at UMT.{" "}
          <button type="button" className="hero-teaser-link" onClick={() => setIsAboutOpen(true)}>
            Read more <IoArrowForward />
          </button>
        </p>

        <UmtRadioCard />
      </div>

      {/* Full "about" copy — kept out of the main flow so the post listing
          (the actual point of the page) isn't pushed down. */}
      <Modal
        open={isAboutOpen}
        onCancel={() => setIsAboutOpen(false)}
        footer={null}
        width={620}
        className="about-modal"
      >
        <h3 className="about-modal-title">About Campus Pulse</h3>
        <p className="about-modal-text">
          The Campus Pulse is your <strong>central hub</strong> for all the latest news, updates,
          and exciting opportunities on campus. From changes in academic schedules to upcoming
          events, policy updates, and more, this is where you will find the latest information.
          Make sure to check in regularly so you never miss out on events, workshops, or
          initiatives that can enrich your experience at <strong>UMT</strong>. Stay informed, get
          involved, and help us make our campus community even more vibrant and dynamic. Your
          participation makes all the difference.
        </p>
      </Modal>

      <AuthButtons
        setUserType={setUserType}
        setIsLoggedIn={setIsLoggedIn}
        onLogout={handleLogout}
        onUserChange={setCurrentUser}
        onCalendarToken={(tokenInfo) => {
          calendarTokenRef.current = tokenInfo;
        }}
      />

      <PostFilterButtons
        selectedPostFor={selectedPostFor}
        handlePostForChange={handlePostForChange}
        isLoggedIn={isLoggedIn}
        userType={userType}
        dateRange={dateRange}
        handleDateChange={handleDateChange}
      />

      {loading ? (
        <div className="skeleton-container">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="skeleton-item">
              <Skeleton
                height={30}
                width={140}
                style={{ marginBottom: "12px" }}
                baseColor={skeletonBaseColor}
                highlightColor={skeletonHighlightColor}
              />
              <Skeleton
                height={20}
                width="80%"
                style={{ marginBottom: "10px" }}
                baseColor={skeletonBaseColor}
                highlightColor={skeletonHighlightColor}
              />
              <Skeleton
                height={140}
                style={{ marginBottom: "10px" }}
                baseColor={skeletonBaseColor}
                highlightColor={skeletonHighlightColor}
              />
              <Skeleton
                height={16}
                width="60%"
                baseColor={skeletonBaseColor}
                highlightColor={skeletonHighlightColor}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="posts-container">
          {filteredTickets.length > 0 ? (
            filteredTickets.map((ticket) => (
              <TicketCard
                key={ticket.ticketId}
                ticket={ticket}
                canRemind={isLoggedIn && (selectedPostFor === 2 || selectedPostFor === 3)}
                isReminded={Boolean(remindedEvents[ticket.ticketId])}
                onToggleReminder={handleToggleReminder}
                likeCount={likeCounts[ticket.ticketId] || 0}
                isLiked={Boolean(likedIds[ticket.ticketId])}
                onToggleLike={handleToggleLike}
              />
            ))
          ) : (
            <div className="empty-state">
              <IoNewspaperOutline />
              <p>No posts here yet — check back soon!</p>
            </div>
          )}
        </div>
      )}

      {filteredTickets.length > 0 && (
        <Pagination
          className="cp-pagination"
          current={currentPage}
          pageSize={pageSize}
          total={total}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      )}
    </div>
  );
};

export default Rssfeed;
